/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor, dlsTextColor} from '../../../theme/theme';

// the feedback tag severities - `high` (red) and `default` (grey, e.g. an
// availability tag like `No Data`) ...
export type SeverityTagSeverity = 'high' | 'default';

export interface SeverityTagProps {
  label: string;
  severity?: SeverityTagSeverity;
}

const useStyles = makeStyles(() => ({
  tag : {
    boxSizing    : 'border-box',
    display      : 'inline-flex',
    alignItems   : 'center',
    padding      : '4px',
    borderRadius : '4px',
    boxShadow    : '0px 1px 2px rgba(0, 0, 0, 0.12)',
    whiteSpace   : 'nowrap'
  },
  high : {
    backgroundColor : dlsColor('paleRed'),
    color           : dlsTextColor('red')
  },
  default : {
    backgroundColor : dlsColor('grey6'),
    color           : dlsTextColor('grey')
  },
  label : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'uppercase'
  }
}));

/**
 * Renders the current theme's feedback tag for a severity - a small,
 * shadowed, uppercase label tinted by its severity (`high`: red on pale
 * red, `default`: grey on light grey), e.g. a fault codes modal's
 * `18 Active Faults` or a sensor's `No Data`.
 *
 * ```typescript
 *
 *   import {SeverityTag} from '@ion/dls/src/components/tags/current/SeverityTag';
 *   ...
 *
 *   <SeverityTag label='18 Active Faults' severity='high' />
 *
 * ```
 *
 * @param   label    The tag's text.
 * @param   severity The tag's severity - `high` by default.
 * @returns          The severity tag.
 */
export const SeverityTag = ({label, severity = 'high'}: SeverityTagProps) => {
  const classes = useStyles();

  return (
    <span className={classnames(classes.tag, classes[severity])}>
      <span className={classes.label}>
        {label}
      </span>
    </span>
  );
};

export default memo(SeverityTag);
